import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Text, View } from '@aws-amplify/ui-react';
import { useNavigate, useParams } from 'react-router';
import GeneralConfig from './GeneralConfig';
import KpisConfig from './KpisConfig';
import './orgConfigPage.css';
import TeamsConfigurator from './TeamsConfigurator';
import CRMConfigV2 from './CRMConfigV2/CRMConfigV2';
import DocuSealConfig from './DocuSealConfig';
import gql from 'graphql-tag';
import { API, Auth } from 'aws-amplify';

const configTabsTemp = {
  general: false,
  kpi: false,
  teams: false,
  crm: false,
  docuseal: false,
};

// Defined outside component so it's stable and not recreated on every render
const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;

  if (typeof obj1 !== 'object') return obj1 === obj2;

  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

const defaultFeatures = {
  route: {
    enabled: false,
    onlyCreatorCanChangeDisposition: false,
    onlyCreatorCanDelete: false,
    onlyCreatorCanEdit: false,
    onlyCreatorCanViewDetails: false,
    enhancedDoorData: false,
    wanderMode: false,
    disableSearch: false,
    radius: 1000,
    maxPinDistance: 10,
    mapboxKey: '',
  },
  feed: {
    enabled: false,
    comment: false,
    like: false,
    uploadVideo: false,
  },
  feedBlast: {
    enabled: false,
    adminOnly: false,
    deafultPostToAll: false,
    scheduleSend: false,
    circle: 'user',
    individual: 'user',
  },
  person: {
    enabled: false,
    uploadPicture: false,
    uploadVideo: false,
  },
  circle: {
    enabled: false,
    uploadPicture: false,
    uploadVideo: false,
  },
  reward: {
    enabled: false,
    uploadPicture: false,
    uploadVideo: false,
  },
  deal: {
    enabled: false,
    disableCircleFilter: false,
    disableUserFilter: false,
    signOnDevice: false,
    signUsingEmail: false,
    disableChangeStage: false,
    noOrderLinesRequiredForSigning: false,
    orderTableFields: [],
    closerEqualsSetter: false,
    showDispositionOnDealsList: false,
    trackDealViews: false,
    trackDealViewsDebounceMinutes: 3,
  },
  schedule: {
    enabled: false,
    autoSchedule: false,
    useRoundRobin: false,
    schedulerRole: [],
    appointmentNotifications: {
      enabled: false,
      triggers: [],
    },
  },
  games: {
    enabled: true,
    bracketCreation: true,
  },
  dashboard: {
    disabled: false,
  },
  webInterface: {
    hideProducts: false,
    hideFeed: false,
    disableScheduling: false,
  },
  estimates: {
    demoMode: true,
  },
  irHumanReport: {
    currency: 'usd',
    demoMode: false,
    pricePerReport: 1000,
  },
};

export default function OrgConfigPage() {
  const navigate = useNavigate();
  const { orgID } = useParams();
  const isUUID = (str) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
  const [activeTab, setActiveTab] = useState({
    ...configTabsTemp,
    general: true,
  });
  const [featuresList, setFeaturesList] = useState(defaultFeatures);
  const [org, setOrg] = useState(null);

  // Shared state for stage categories across tabs
  const [stageCategories, setStageCategories] = useState({});

  // Add state for tracking original values and changes
  const [originalFeaturesList, setOriginalFeaturesList] =
    useState(defaultFeatures);
  const [originalOrg, setOriginalOrg] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Helper function for deep equality comparison — defined at module level above

  // Check for changes whenever state updates
  // Use updatedAt + id as a lightweight proxy instead of deep-comparing the
  // entire org object (which can be very large and cause render hangs).
  React.useEffect(() => {
    if (!originalOrg || !originalFeaturesList) {
      setHasChanges(false);
      return;
    }

    const orgChanged =
      org?.updatedAt !== originalOrg?.updatedAt || org?.id !== originalOrg?.id;
    const featuresChanged = !deepEqual(featuresList, originalFeaturesList);

    setHasChanges(orgChanged || featuresChanged);
  }, [
    org?.id,
    org?.updatedAt,
    featuresList,
    originalOrg,
    originalFeaturesList,
  ]);

  async function getOrgData(orgID) {
    try {
      const myQuery = gql`
        query GetOrg($id: ID!) {
          getOrg(id: $id) {
            id
            name
            domains
            contactEmail
            contactName
            contactAddress
            contactAddress2
            contactCity
            contactState
            contactZip
            contactPhone
            configParms
            hubspotID
            restrictionType
            docuSealAccess
            users {
              items {
                id
                userID
                orgID
                status
                memberType
                orgPoints
                lifetimeOrgPoints
                isActive
                createdBy
                lastUpdatedBy
                createdAt
                updatedAt
                isDeleted
              }
              nextToken
            }
            preferences {
              items {
                id
                orgID
                isHidden
                preferenceType
                name
                entityType
                entityID
                preferences
                createdAt
                updatedAt
              }
              nextToken
            }
            permissions
            paymentCustomerID
            billingType
            planType
            salesChannel
            selfServiceCancellation
            bypassBillingCheck
            trials {
              length
              startDate
              type
              features {
                type
                count
              }
            }
            createdBy
            lastUpdatedBy
            createdAt
            updatedAt
            isDeleted
          }
        }
      `;
      const res = await API.graphql({
        query: myQuery,
        variables: { id: orgID },
      });
      if (res.data?.getOrg) {
        setOrg(res.data.getOrg);
      } else {
        console.error('OrgConfigPage: getOrg returned no data', res);
      }
      // orgStatesSetter(res.data.getOrg);
    } catch (err) {
      console.error('OrgConfigPage: error fetching org data', err);
    }
  }

  useEffect(() => {
    if (orgID) {
      getOrgData(orgID);
    }
  }, []);

  useEffect(() => {
    if (org?.configParms) {
      try {
        const configParms = JSON.parse(org.configParms);
        if (configParms.features) {
          const dbFeatures = configParms.features;
          const mergedFeatures = JSON.parse(JSON.stringify(defaultFeatures)); // Deep copy for defaultFeatures

          for (const featureKey in dbFeatures) {
            if (Object.prototype.hasOwnProperty.call(dbFeatures, featureKey)) {
              if (
                typeof mergedFeatures[featureKey] === 'object' &&
                mergedFeatures[featureKey] !== null &&
                !Array.isArray(mergedFeatures[featureKey]) &&
                typeof dbFeatures[featureKey] === 'object'
              ) {
                // It's an object, merge it but filter out undefined values
                const filteredDbFeatures = {};
                for (const prop in dbFeatures[featureKey]) {
                  if (dbFeatures[featureKey][prop] !== undefined) {
                    filteredDbFeatures[prop] = dbFeatures[featureKey][prop];
                  }
                }
                mergedFeatures[featureKey] = {
                  ...mergedFeatures[featureKey],
                  ...filteredDbFeatures,
                };
              } else {
                // It's a new feature or not an object, just assign if not undefined
                if (dbFeatures[featureKey] !== undefined) {
                  mergedFeatures[featureKey] = dbFeatures[featureKey];
                }
              }
            }
          }
          setFeaturesList(mergedFeatures);
          setOriginalFeaturesList(mergedFeatures); // Set original for comparison
        }
        if (!originalOrg) {
          setOriginalOrg(org); // Set original org for comparison only once
        }
      } catch (e) {
        console.error('Error parsing configParms', e);
      }
    }
  }, [org?.id]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const groups =
          cognitoUser.signInUserSession?.accessToken?.payload?.[
            'cognito:groups'
          ] || [];
        if (!groups.includes('paiv_admin_rw')) {
          navigate('/');
        }
      } catch {
        navigate('/');
      }
    };
    checkAccess();
  }, []);

  const tabClick = (e) => {
    const val = e.currentTarget.getAttribute('name');
    setActiveTab(() => ({ ...configTabsTemp, [val]: true }));
  };

  // Reset function to restore original values
  const handleReset = () => {
    if (originalOrg && originalFeaturesList) {
      setOrg(originalOrg);
      setFeaturesList(originalFeaturesList);
    }
  };

  // Save handler that updates original values after successful save
  const handleSaveSuccess = (updatedOrg, updatedFeatures) => {
    setOriginalOrg(updatedOrg);
    setOriginalFeaturesList(updatedFeatures);
  };

  return (
    <View
      className="org-config-page"
      style={{
        padding: '100px 5px 5px 5px',
        borderRadius: '5px',
      }}
    >
      <Box
        style={{
          display: 'flex',
          padding: '0',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box id="back-to-list-button" onClick={() => navigate('/admin-config')}>
          <Typography
            style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              marginLeft: '-12px',
            }}
          >
            ← Orgs List
          </Typography>
        </Box>
        <Text
          style={{ fontWeight: 'bold', fontSize: '2rem', color: '#FF6A00' }}
        >
          {isUUID(orgID) ? org?.name : orgID}
        </Text>
        <Box style={{ width: '100px' }} />
      </Box>
      <Box id="tabs-box">
        <Box
          className={`config-tab ${
            activeTab.general ? 'active' : 'non-active'
          }`}
          name="general"
          onClick={tabClick}
        >
          General
        </Box>
        <Box
          className={`config-tab ${activeTab.kpi ? 'active' : 'non-active'}`}
          name="kpi"
          onClick={tabClick}
        >
          KPI
        </Box>
        <Box
          className={`config-tab ${activeTab.crm ? 'active' : 'non-active'}`}
          name="crm"
          onClick={tabClick}
        >
          CRM
        </Box>
        <Box
          className={`config-tab ${activeTab.teams ? 'active' : 'non-active'}`}
          name="teams"
          onClick={tabClick}
        >
          TEAMS
        </Box>
        <Box
          className={`config-tab ${
            activeTab.docuseal ? 'active' : 'non-active'
          }`}
          name="docuseal"
          onClick={tabClick}
        >
          DocuSeal
        </Box>
      </Box>
      <Box
        style={{
          border: '1px solid black',
          padding: '15px',
          backgroundColor: 'white',
        }}
      >
        {activeTab.general && (
          <GeneralConfig
            org={org}
            setOrg={setOrg}
            orgID={orgID}
            featuresList={featuresList}
            setFeaturesList={setFeaturesList}
            hasChanges={hasChanges}
            onReset={handleReset}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
        {activeTab.kpi && <KpisConfig orgID={orgID} />}
        {activeTab.crm && (
          <CRMConfigV2
            org={org}
            setOrg={setOrg}
            orgID={orgID}
            featuresList={featuresList}
            setFeaturesList={setFeaturesList}
            stageCategories={stageCategories}
            setStageCategories={setStageCategories}
          />
        )}
        {activeTab.crm && (
          <>
            <View style={{ height: 150 }} />
            {/* <CRMConfig orgID={orgID} /> */}
          </>
        )}
        {activeTab.teams && <TeamsConfigurator orgID={orgID} />}
        {activeTab.docuseal && (
          <DocuSealConfig
            org={org}
            orgID={orgID}
            stageCategories={stageCategories}
          />
        )}
      </Box>
    </View>
  );
}
